import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deleteSavedReport, getSavedReports } from "@/api/reports";

import {
  SavedReport,
  SaveReportFn,
  SaveReportPayload,
} from "./savedReports.types";

const POLL_INTERVAL_MS = 5000;

const savedReportsQueryKeys = {
  base: ["reports:saved-reports"] as const,
  list: (report_id: number) =>
    ["reports:saved-reports", "list", { report_id }] as const,
};

const useGetSavedReports = (report_id?: number) =>
  useQuery<SavedReport[]>({
    queryKey: savedReportsQueryKeys.list(report_id as number),
    queryFn: async () => {
      const res = await getSavedReports({ report_id });
      // connection.js never rejects, failures resolve as { success: false }
      if (!Array.isArray(res)) throw new Error("Failed to load saved reports");
      return res;
    },
    enabled: !!report_id,
    // The axios interceptor already toasts each failure, so retries would
    // stack identical toasts
    retry: false,
    // Poll only while a report is still generating
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.is_ready === 0)
        ? POLL_INTERVAL_MS
        : false,
  });

const useSaveReport = (saveFn: SaveReportFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveReportPayload) => {
      const res = (await saveFn(payload)) as { success?: boolean } | undefined;
      if (res?.success === false) throw new Error("Failed to save report");
      return res;
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: savedReportsQueryKeys.base }),
  });
};

const useDeleteSavedReport = (report_id: number) => {
  const queryClient = useQueryClient();
  const key = savedReportsQueryKeys.list(report_id);
  return useMutation({
    mutationFn: async (saved_report_id: number) => {
      const res = await deleteSavedReport({ saved_report_id });
      // connection.js never rejects, failures resolve as { success: false }
      if (res?.success === false)
        throw res.error ?? new Error("Failed to delete saved report");
      return res;
    },
    // Optimistic removal: DELETE /reports/saved-reports responds before the
    // row is deleted (ENG-2139), so success must not invalidate and refetch.
    // Until that is fixed, a poll or refresh that lands before the delete
    // finishes can bring the removed row back.
    onMutate: async (saved_report_id) => {
      await queryClient.cancelQueries({ queryKey: key });
      const removed = queryClient
        .getQueryData<SavedReport[]>(key)
        ?.find((r) => r.saved_report_id === saved_report_id);
      queryClient.setQueryData<SavedReport[]>(key, (old) =>
        old?.filter((r) => r.saved_report_id !== saved_report_id)
      );
      return { removed };
    },
    // Re-insert only the failed row, so a rollback cannot resurrect rows
    // removed by other deletes that are still in flight. Newest first, like
    // the API (ORDER BY saved_report_id DESC).
    onError: (_err, saved_report_id, ctx) => {
      const removed = ctx?.removed;
      if (!removed) return;
      queryClient.setQueryData<SavedReport[]>(key, (old) =>
        old && !old.some((r) => r.saved_report_id === saved_report_id)
          ? [...old, removed].sort(
              (a, b) => b.saved_report_id - a.saved_report_id
            )
          : old
      );
    },
  });
};

export const SavedReportsQuery = {
  queryKeys: savedReportsQueryKeys,
  useGetSavedReports,
  useSaveReport,
  useDeleteSavedReport,
};
